'use client';

import { Button, Text } from '@/design-system';
import type { IssuedCoupon } from '../../api/user/dto/response/app-onboarding-benefit.res.dto';
import { formattedDate } from '../../utils/date/formattedDate';
import BottomDrawerV2 from '../BottomDrawerV2';
import UImage from '../UImage';

const COUPON_COMPLETE_IMAGE_URL = `${process.env.NEXT_PUBLIC_S3_IMAGE_URL}/assets/app-install-promotion/app_install_coupon_complete.png`;

interface WebToAppCouponIssuedModalProps {
  /** 발급된 쿠폰 정보 (만료 안내에 사용) */
  coupon: IssuedCoupon | null;

  /** "앱에서 사용하기" 클릭 — 앱(스토어)으로 이동하고 재노출 쿨다운을 시작한다 */
  onClickUseInApp: () => void;

  /** 발급 완료 모달 닫기 — 재노출 쿨다운을 시작한다 */
  onClose: () => void;
}

/**
 * 앱 전용 쿠폰 발급 완료 안내 바텀시트
 *
 * 웹→앱 설치 모달에서 "쿠폰 받기"를 누른 직후 노출된다. 발급된 5,000원 쿠폰을 안내하고
 * "앱에서 사용하기" CTA로 앱(스토어)으로 유도한다.
 */
const WebToAppCouponIssuedModal = ({
  coupon,
  onClickUseInApp,
  onClose,
}: WebToAppCouponIssuedModalProps) => {
  const expirationGuide = coupon
    ? `*${formattedDate(new Date(coupon.expiredAt), 'yyyy.MM.dd')}까지 사용 가능`
    : '*유효기간 7일';

  return (
    <BottomDrawerV2.Wrapper
      open
      onClose={onClose}
      borderRadius={16}
      fitContent
      backdropBlur={false}
    >
      <BottomDrawerV2.Header handleClose={onClose} />
      <div className="flex flex-col items-center gap-[20px] px-[16px] pb-[24px]">
        <div className="flex flex-col items-center gap-[8px] text-center">
          <Text variant="h3-bold" as="p" className="text-gray-900">
            앱 전용 쿠폰 발급 완료!
          </Text>
          <Text variant="h6-medium-140" as="p" className="text-gray-600">
            어글리어스 앱을 다운로드 받아주세요.
          </Text>
        </div>
        <div className="flex flex-col items-center gap-[8px]">
          <UImage
            src={COUPON_COMPLETE_IMAGE_URL}
            alt="앱 전용 5,000원 쿠폰"
            width={238}
            height={140}
          />
          <Text variant="h7-regular-100" as="p" className="text-gray-600">
            {expirationGuide}
          </Text>
        </div>
      </div>
      <div className="px-[16px] pb-[16px]">
        {/* 설치 모달과 동일한 soil-brown CTA — primary(brand-1) 배경만 오버라이드 */}
        <Button
          width="full"
          className="bg-soil-brown-700"
          onClick={onClickUseInApp}
        >
          앱에서 사용하기
        </Button>
      </div>
    </BottomDrawerV2.Wrapper>
  );
};

export default WebToAppCouponIssuedModal;
